/* Product page behaviour: gallery and zoom, variant picker, add to bag,
   size guide dialog, share link and product recommendations.
   Loaded with defer by sections/main-product.liquid. */

(() => {
  if (window.themeProductLoaded) return;
  window.themeProductLoaded = true;

  const strings = () => window.themeStrings || {};
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const lockScroll = (locked) => window.theme && window.theme.lockScroll(locked);
  const formatMoney = (cents) => (window.theme ? window.theme.formatMoney(cents) : (cents / 100).toFixed(2));

  /* Opens a <dialog> and restores scroll and focus when it closes. */
  function openDialog(dialog, trigger) {
    if (!dialog || typeof dialog.showModal !== 'function' || dialog.open) return;
    dialog.showModal();
    lockScroll(true);
    dialog.addEventListener('close', () => {
      lockScroll(false);
      if (trigger && document.contains(trigger)) trigger.focus({ preventScroll: true });
    }, { once: true });
  }

  /* Size guide and other [data-modal-open] links: open the dialog instead of following the link. */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-modal-open]');
    if (!opener) return;
    const dialog = document.getElementById(opener.dataset.modalOpen);
    if (!dialog || typeof dialog.showModal !== 'function') return;
    event.preventDefault();
    openDialog(dialog, opener);
  });

  /* Gallery: slider with arrows and thumbnails, jump to the variant's image on change, zoom viewer. */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;
      this.counter = this.querySelector('.product-gallery__counter');
      this.counterCurrent = this.querySelector('[data-counter-current]');
      this.counterLive = this.querySelector('[data-counter-live]');
      this.dialog = this.querySelector('dialog');
      this.stage = this.querySelector('[data-gallery-stage]');
      this.thumbStrip = this.querySelector('[data-gallery-thumbs]');
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb-for]'));
      this.arrows = Array.from(this.querySelectorAll('[data-gallery-step]'));
      this.current = 0;

      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.sync();
        });
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      this.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-thumb-for]');
        if (thumb) {
          this.goTo(this.thumbs.indexOf(thumb));
          return;
        }
        const arrow = event.target.closest('[data-gallery-step]');
        if (arrow) this.goTo(this.current + Number(arrow.dataset.galleryStep));
      });

      if (this.stage) {
        this.stage.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          this.goTo(this.current + (event.key === 'ArrowRight' ? 1 : -1));
        });
      }

      this.addEventListener('click', (event) => {
        const link = event.target.closest('[data-zoom-open]');
        if (!link || !this.dialog) return;
        event.preventDefault();
        this.openZoom(link.closest('[data-media-id]'), link);
      });

      if (this.dialog) {
        this.figure = this.dialog.querySelector('[data-zoom-figure]');
        this.zoomCount = this.dialog.querySelector('[data-zoom-count]');
        this.dialog.addEventListener('click', (event) => {
          const step = event.target.closest('[data-zoom-step]');
          if (step) this.stepZoom(Number(step.dataset.zoomStep));
        });
        this.dialog.addEventListener('pointerdown', (event) => {
          this.swipeStart = event.pointerType === 'touch' ? event.clientX : null;
        });
        this.dialog.addEventListener('pointerup', (event) => {
          if (this.swipeStart == null) return;
          const delta = event.clientX - this.swipeStart;
          this.swipeStart = null;
          if (Math.abs(delta) > 50) this.stepZoom(delta < 0 ? 1 : -1);
        });
        this.dialog.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.stepZoom(-1);
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.stepZoom(1);
          }
        });
      }

      this.onVariantChange = (event) => {
        const { sectionId, variant } = event.detail || {};
        if (sectionId !== this.dataset.section || !variant || !variant.featured_media) return;
        this.setActiveMedia(variant.featured_media.id);
      };
      document.addEventListener('variant:change', this.onVariantChange);
    }

    disconnectedCallback() {
      document.removeEventListener('variant:change', this.onVariantChange);
    }

    items() {
      return Array.from(this.track.children);
    }

    /* Works out which slide is in view, then updates the counter, thumbnails and arrows to match. */
    sync() {
      const items = this.items();
      if (!items.length) return;
      const left = this.track.scrollLeft;
      let index = 0;
      let best = Infinity;
      items.forEach((item, i) => {
        const distance = Math.abs(item.offsetLeft - left);
        if (distance < best) {
          best = distance;
          index = i;
        }
      });
      if (index === this.current) return;
      this.current = index;
      const total = items.length;

      if (this.counterCurrent) this.counterCurrent.textContent = index + 1;
      if (this.counterLive && this.counter.dataset.template) {
        this.counterLive.textContent = this.counter.dataset.template.replace('__CURRENT__', index + 1).replace('__TOTAL__', total);
      }

      this.thumbs.forEach((thumb, i) => {
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      this.arrows.forEach((arrow) => {
        arrow.disabled = Number(arrow.dataset.galleryStep) < 0 ? index === 0 : index === total - 1;
      });
      this.revealThumb(this.thumbs[index]);
    }

    /* Keeps the current thumbnail in view inside its strip without scrolling the page. */
    revealThumb(thumb) {
      if (!thumb || !this.thumbStrip) return;
      const strip = this.thumbStrip;
      const item = thumb.parentElement;
      const target = item.offsetLeft - (strip.clientWidth - item.offsetWidth) / 2;
      strip.scrollTo({ left: Math.max(0, target), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    goTo(index) {
      const items = this.items();
      if (!items.length) return;
      const target = items[Math.max(0, Math.min(index, items.length - 1))];
      const img = target.querySelector('img');
      if (img) img.loading = 'eager';
      this.track.scrollTo({ left: target.offsetLeft, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    setActiveMedia(mediaId) {
      const item = this.track.querySelector(`[data-media-id="${mediaId}"]`);
      if (item) this.goTo(this.items().indexOf(item));
    }

    zoomItems() {
      return this.items().filter((item) => item.querySelector('[data-zoom-open]'));
    }

    openZoom(item, trigger) {
      this.zoomList = this.zoomItems();
      if (!this.zoomList.length) return;
      this.zoomIndex = Math.max(0, this.zoomList.indexOf(item));
      this.renderZoom();
      openDialog(this.dialog, trigger);
    }

    stepZoom(delta) {
      if (!this.zoomList || this.zoomList.length < 2) return;
      const total = this.zoomList.length;
      this.zoomIndex = (this.zoomIndex + delta + total) % total;
      this.renderZoom();
    }

    renderZoom() {
      const total = this.zoomList.length;
      const link = this.zoomList[this.zoomIndex].querySelector('[data-zoom-open]');
      const source = link.querySelector('img');
      const img = document.createElement('img');
      if (source && source.srcset) {
        img.srcset = source.srcset;
        img.sizes = '100vw';
      }
      img.src = link.href;
      img.alt = source ? source.alt : '';
      if (source) {
        img.width = source.width;
        img.height = source.height;
      }
      this.figure.replaceChildren(img);
      if (this.zoomCount) this.zoomCount.textContent = `${this.zoomIndex + 1} / ${total}`;
      this.dialog.querySelectorAll('[data-zoom-step]').forEach((button) => {
        button.hidden = total < 2;
      });
    }
  }
  if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);

  /* Info column: sticks below the header; when taller than the viewport it sticks by its bottom edge instead. */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('data-sticky') || !('ResizeObserver' in window)) return;
      this.update = () => {
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 64;
        const top = header + 24;
        const height = this.offsetHeight;
        const fits = height <= window.innerHeight - top - 24;
        this.style.setProperty('--sticky-top', `${fits ? top : Math.round(window.innerHeight - height - 24)}px`);
      };
      this.observer = new ResizeObserver(() => this.update());
      this.observer.observe(this);
      this.onResize = window.theme ? window.theme.debounce(this.update, 100) : this.update;
      window.addEventListener('resize', this.onResize);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.onResize) window.removeEventListener('resize', this.onResize);
    }
  }
  if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);

  /* Variant picker: matches the selected options against the embedded variants JSON. */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-variants]');
      if (!data) return;
      this.section = this.dataset.section;
      this.variants = JSON.parse(data.textContent);
      this.fieldsets = Array.from(this.querySelectorAll('fieldset[data-option-index]'));
      this.fallbackSwatches();
      this.updateAvailability(this.selectedOptions());
      this.addEventListener('change', (event) => {
        if (event.target.matches('input[type="radio"]')) this.onChange(event.target);
      });
    }

    /* Colour values without a swatch use their name as a CSS colour, or become a text pill.
       If any value in an option can't be guessed, the whole option uses text pills so the row stays consistent. */
    fallbackSwatches() {
      this.fieldsets.forEach((fieldset) => {
        const labels = Array.from(fieldset.querySelectorAll('[data-swatch-guess]'));
        const unguessable = labels.some((label) => {
          const guess = label.dataset.swatchGuess;
          return !guess || !window.CSS || !CSS.supports('color', guess);
        });
        if (unguessable) labels.forEach((label) => label.classList.add('swatch--text'));
      });
    }

    selectedOptions() {
      return this.fieldsets.map((fieldset) => {
        const checked = fieldset.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    onChange(input) {
      const fieldset = input.closest('fieldset');
      const label = fieldset && fieldset.querySelector('[data-selected-value]');
      if (label) label.textContent = input.value;
      const selected = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((value, i) => value === selected[i]));
      this.updateAvailability(selected);
      this.updateUrl(variant);
      this.updateForm(variant);
      this.updatePrice(variant);
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { sectionId: this.section, variant: variant || null } }));
    }

    /* A value is available when some in-stock variant has it together with the options chosen above it. */
    updateAvailability(selected) {
      this.fieldsets.forEach((fieldset, index) => {
        const before = selected.slice(0, index);
        fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
          const available = this.variants.some((v) => v.available && v.options[index] === input.value && before.every((value, j) => v.options[j] === value));
          const label = fieldset.querySelector(`label[for="${input.id}"]`);
          if (!label) return;
          label.classList.toggle('is-unavailable', !available);
          const note = label.querySelector('[data-unavailable-note]');
          if (note) note.hidden = available;
        });
      });
    }

    updateUrl(variant) {
      if (!variant || !this.dataset.url) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState(window.history.state, '', url.toString());
    }

    updateForm(variant) {
      const form = document.querySelector(`product-form[data-section="${this.section}"]`);
      const idInput = form && form.querySelector('input[name="id"]');
      if (idInput && variant) idInput.value = variant.id;
      const button = document.getElementById(`AddToCart-${this.section}`);
      if (!button) return;
      const text = button.querySelector('[data-add-text]') || button;
      const s = strings();
      if (!variant) {
        button.disabled = true;
        text.textContent = s.unavailable || 'Unavailable';
      } else if (!variant.available) {
        button.disabled = true;
        text.textContent = s.soldOut || 'Sold out';
      } else {
        button.disabled = false;
        text.textContent = s.addToCart || 'Add to bag';
      }
      const paymentButton = form && form.querySelector('.shopify-payment-button');
      if (paymentButton) paymentButton.hidden = !variant || !variant.available;
      if (form && typeof form.clearError === 'function') form.clearError();
    }

    updatePrice(variant) {
      const container = document.getElementById(`Price-${this.section}`);
      const price = container && container.querySelector('[data-price]');
      if (!price || !variant) return;
      const onSale = variant.compare_at_price > variant.price;
      const hidden = (text) => {
        const span = document.createElement('span');
        span.className = 'visually-hidden';
        span.textContent = text;
        return span;
      };
      const current = document.createElement('span');
      current.className = 'price__current';
      current.textContent = formatMoney(variant.price);
      const nodes = [hidden(onSale ? container.dataset.saleLabel : container.dataset.regularLabel), current];
      if (onSale) {
        const compare = document.createElement('s');
        compare.className = 'price__compare';
        compare.textContent = formatMoney(variant.compare_at_price);
        nodes.push(hidden(container.dataset.regularLabel), compare);
      }
      price.classList.toggle('price--on-sale', onSale);
      price.replaceChildren(...nodes);
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* Product form: adds to the bag with fetch, then hands off to the cart drawer (or the cart page). */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.button = this.form.querySelector('[type="submit"]');
      this.error = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    setLoading(loading) {
      if (!this.button) return;
      this.button.classList.toggle('is-loading', loading);
      this.button.setAttribute('aria-busy', loading ? 'true' : 'false');
    }

    showError(message) {
      if (!this.error) return;
      this.error.hidden = false;
      this.error.textContent = message;
    }

    clearError() {
      if (!this.error) return;
      this.error.hidden = true;
      this.error.textContent = '';
    }

    async onSubmit(event) {
      event.preventDefault();
      if (!this.button || this.button.disabled || this.button.classList.contains('is-loading')) return;
      this.clearError();
      this.setLoading(true);

      const formData = new FormData(this.form);
      const quantityInput = document.getElementById(`Quantity-${this.dataset.section}`);
      const quantity = Number(formData.get('quantity') || (quantityInput && quantityInput.value) || 1);
      const body = {
        id: Number(formData.get('id')),
        quantity,
        sections: 'cart-drawer',
        sections_url: window.location.pathname,
      };

      try {
        const config = window.theme ? window.theme.fetchConfig() : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
        const response = await fetch(`${window.routes.cart_add_url}.js`, { ...config, body: JSON.stringify(body) });
        const data = await response.json();
        if (!response.ok || data.status) {
          this.showError(data.description || data.message || strings().cartError);
          return;
        }
        if (window.themeSettings && window.themeSettings.cartType === 'page') {
          window.location.href = window.routes.cart_url;
          return;
        }
        const cart = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, source: 'product-form', sections: data.sections, open: true } }));
      } catch (error) {
        this.showError(strings().cartError);
      } finally {
        this.setLoading(false);
      }
    }
  }
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  /* Share: copies the product link (with the selected variant) and confirms it. */
  class ShareLink extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.label = this.querySelector('[data-share-label]');
      this.status = this.querySelector('[data-share-status]');
      if (!this.button || !this.label) return;
      this.defaultLabel = this.label.textContent;
      this.button.addEventListener('click', () => this.copy());
    }

    async copy() {
      const url = new URL(this.dataset.url || window.location.href, window.location.origin);
      const variant = new URL(window.location.href).searchParams.get('variant');
      if (variant) url.searchParams.set('variant', variant);
      try {
        await navigator.clipboard.writeText(url.toString());
        this.show(this.dataset.copied, true);
      } catch (error) {
        this.show(this.dataset.failed, false);
      }
    }

    show(message, copied) {
      clearTimeout(this.timer);
      this.classList.toggle('is-copied', copied);
      if (copied) this.label.textContent = message;
      if (this.status) this.status.textContent = message;
      this.timer = setTimeout(() => {
        this.classList.remove('is-copied');
        this.label.textContent = this.defaultLabel;
        if (this.status) this.status.textContent = '';
      }, 2500);
    }
  }
  if (!customElements.get('share-link')) customElements.define('share-link', ShareLink);

  /* Recommendations: complementary first, related as a fallback, removed when both are empty. */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.loaded) return;
      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      }, { rootMargin: '0px 0px 400px 0px' });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    async fetchResults(url) {
      const response = await fetch(url);
      if (!response.ok) return null;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const element = doc.querySelector('product-recommendations');
      return element && element.querySelector('[data-recommendations-results]') ? element.innerHTML : null;
    }

    async load() {
      this.loaded = true;
      try {
        let html = await this.fetchResults(this.dataset.url);
        if (!html && this.dataset.url.includes('intent=complementary')) {
          html = await this.fetchResults(this.dataset.url.replace('intent=complementary', 'intent=related'));
        }
        if (!html) {
          this.removeSection();
          return;
        }
        this.innerHTML = html;
        if (window.themeMotion && typeof window.themeMotion.reveals === 'function') window.themeMotion.reveals(this);
      } catch (error) {
        this.removeSection();
      }
    }

    removeSection() {
      (this.closest('.shopify-section') || this).remove();
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);
})();
